import React, { useEffect, useRef, useState } from "react";
import { ArrowLeft, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/* ---------------- Mensajes con alumnos ---------------- */
export default function DocenteMessages({ initialStudentId = null, compact = false }) {
  const [threads, setThreads] = useState(null);
  const [active, setActive] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  const loadThreads = () => api.get("/docente/student-messages").then(({ data }) => setThreads(data.threads || [])).catch(() => setThreads([]));
  useEffect(() => { if (initialStudentId) openThread({ student_id: initialStudentId }); }, [initialStudentId]); // eslint-disable-line
  useEffect(() => { loadThreads(); const iv = setInterval(loadThreads, 8000); return () => clearInterval(iv); }, []);

  const openThread = (t) => { setActive(t); api.get(`/docente/student-messages/${t.student_id}`).then(({ data }) => { setMsgs(data.messages || []); setActive({ ...t, ...data.student }); }).catch(() => setMsgs([])); };
  useEffect(() => {
    if (!active) return;
    const iv = setInterval(() => api.get(`/docente/student-messages/${active.student_id}`).then(({ data }) => setMsgs(data.messages || [])).catch(() => {}), 5000);
    return () => clearInterval(iv);
  }, [active?.student_id]); // eslint-disable-line
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const send = async () => {
    const t = text.trim(); if (!t || !active) return;
    setBusy(true); setText("");
    try { const { data } = await api.post(`/docente/student-messages/${active.student_id}`, { text: t }); setMsgs((m) => [...m, data]); }
    catch { toast.error("No se pudo enviar"); setText(t); } finally { setBusy(false); }
  };

  if (threads === null) return <div className="py-16 text-center text-nardo-500"><Loader2 className="w-6 h-6 animate-spin inline" /></div>;

  if (active) {
    return (
      <div className={`bg-white rounded-2xl border border-nardo-200 shadow-sm flex flex-col ${compact ? "h-full rounded-t-none" : "h-[65vh]"}`} data-testid="docente-chat-thread">
        <div className="flex items-center gap-3 px-4 py-3 border-b border-nardo-100 bg-navy/5 rounded-t-2xl">
          <button onClick={() => { setActive(null); loadThreads(); }} data-testid="docente-chat-back" className="text-nardo-500 hover:text-navy"><ArrowLeft className="w-5 h-5" /></button>
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-navy-light to-navy text-white flex items-center justify-center font-bold">{(active.full_name || active.student_name || "A").slice(0, 1)}</div>
          <div><div className="font-bold text-navy text-sm">{active.full_name || active.student_name}</div><div className="text-[11px] text-nardo-400">{active.group_label || ""}</div></div>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-2 bg-nardo-50/40">
          {msgs.length === 0 && <div className="text-center text-nardo-400 text-sm py-10">Sin mensajes todavía.</div>}
          {msgs.map((m) => (
            <div key={m.id} className={`flex ${m.sender === "docente" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm whitespace-pre-wrap break-words ${m.sender === "docente" ? "bg-navy text-white rounded-br-sm" : "bg-white border border-nardo-200 text-navy rounded-bl-sm"}`}>{m.text}<div className={`text-[9px] mt-1 text-right ${m.sender === "docente" ? "text-white/70" : "text-nardo-400"}`} data-testid={`msg-time-${m.id}`}>{m.created_at ? new Date(m.created_at).toLocaleString("es-MX", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : ""}</div></div>
            </div>
          ))}
          <div ref={endRef} />
        </div>
        <div className="p-3 border-t border-nardo-100 flex items-center gap-2">
          <textarea value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey && window.matchMedia("(pointer: fine)").matches) { e.preventDefault(); send(); } }} rows={2} placeholder="Responde al alumno…" data-testid="docente-chat-input" className="flex-1 border border-nardo-200 rounded-2xl px-4 py-2.5 text-base resize-none max-h-32 outline-none focus:border-navy" />
          <button onClick={send} disabled={busy || !text.trim()} data-testid="docente-chat-send" className="w-10 h-10 rounded-full bg-navy hover:bg-navy-light text-white flex items-center justify-center disabled:opacity-40">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}</button>
        </div>
      </div>
    );
  }

  if (threads.length === 0) return <div className="py-16 text-center text-nardo-500" data-testid="docente-messages-empty">Aún no tienes mensajes de tus alumnos.</div>;
  return (
    <div className="space-y-2" data-testid="docente-threads-list">
      {threads.map((t) => (
        <button key={t.student_id} onClick={() => openThread(t)} data-testid={`docente-thread-${t.student_id}`} className="w-full flex items-center gap-3 bg-white rounded-2xl border border-nardo-200 shadow-sm hover:shadow-md p-3 text-left transition-all">
          <div className="w-11 h-11 rounded-full bg-gradient-to-br from-navy-light to-navy text-white flex items-center justify-center font-bold shrink-0">{(t.student_name || "A").slice(0, 1)}</div>
          <div className="flex-1 min-w-0"><div className="font-bold text-navy text-sm truncate">{t.student_name}</div><div className="text-[12px] text-nardo-400 truncate">{t.last_text}</div></div>
          {t.unread > 0 && <span className="bg-red-500 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center shrink-0">{t.unread}</span>}
        </button>
      ))}
    </div>
  );
}
